import {
  Avatar,
  Button,
  HStack,
  Icon,
  SimpleGrid,
  SkeletonCircle,
  SkeletonText,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  RiEye2Line,
  RiEyeLine,
  RiHeart2Line,
  RiMapPin2Line,
  RiStore2Line,
} from "@remixicon/react";
import { lazy, Suspense, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ButtonCopyUrl } from "../../../components/button/ButtonCopyUrl";
import { TitlePageDashboard } from "../../../components/button/TitlePageDashboard";
import { CardInfo } from "../../../components/card/CardInfo";
import { MyStoreProductInfo } from "../../../components/dashboard_reseller/MyStoreProductInfo";
import { CardSkeleton } from "../../../components/skeleton/CardSkeleton";
import { MyStoreDetailProductInterface } from "../../../constants/MyStore";
import useCustomToast from "../../../lib/useCustomToast";
import useScreenWidth from "../../../lib/useScreenWidth";
import { useDarkWhiteColor } from "../../../themes/colors";
import { axiosReseller } from "../../../utils/AxiosInstance";
import formatNumber, { splitCityName } from "../../../utils/helperFunction";

const AnalyticLineChart = lazy(
  () => import("../../../components/chart/AnalyticLineChart")
);

export const MyStoreDetailProduct = () => {
  const [data, setData] = useState<MyStoreDetailProductInterface | undefined>(
    undefined
  );
  const [loaded, setLoaded] = useState<boolean>(false);
  const navigate = useNavigate();
  const { id } = useParams();
  const bgBnw = useDarkWhiteColor();
  const sw = useScreenWidth();
  const toast = useCustomToast();

  useEffect(() => {
    axiosReseller
      .get(`/v1/reseller/product/${id}`)
      .then((res) => {
        setData(res.data.data);
      })
      .catch((err) => {
        toast({
          description: err?.response?.data?.message || "",
        });
      })
      .finally(() => {
        setLoaded(true);
      });
  }, [id]);

  const goToPreviewProduct = () => {
    navigate(`/product/${data?.store.prefix}/${data?.product?.slug}`);
  };

  return (
    <VStack
      className="detail-product"
      w={"100%"}
      minH={"calc(100dvh - 60px)"}
      p={4}
      bg={bgBnw}
      align={"stretch"}
    >
      <Stack
        flexDir={["column", "column", "row", "row", "row"]}
        justify={"space-between"}
      >
        <TitlePageDashboard title="Detail Produk" isSubPage={true} />

        <HStack>
          <ButtonCopyUrl
            btnText="Salin Link Produk"
            variant={"solid"}
            storeId={data?.store?.prefix}
            productId={data?.product?.slug}
            isDisabled={
              data?.store.prefix && data?.product?.slug ? false : true
            }
          />

          <Button
            variant={"ghost"}
            colorScheme="red"
            leftIcon={<Icon as={RiEye2Line} />}
            onClick={goToPreviewProduct}
          >
            Preview Produk
          </Button>
        </HStack>
      </Stack>

      <Stack w={"100%"} direction={sw > 768 ? "row" : "column"} mt={4} gap={4}>
        <VStack
          className="profile"
          p={4}
          borderWidth={"1px"}
          borderRadius={"md"}
          flex={1}
          justify={"center"}
          textAlign={"center"}
        >
          <SkeletonCircle isLoaded={loaded} size={["12", null, "20"]}>
            <Avatar
              name={data?.store.name || ""}
              src={
                data?.store.logo
                  ? `${process.env.REACT_APP_MEDIA_URL}${data.store.logo} `
                  : ""
              }
              size={["md", null, "lg"]}
            />
          </SkeletonCircle>

          <SkeletonText isLoaded={loaded} noOfLines={loaded ? undefined : 1}>
            <Text textStyle={"h-reg-title"}>{data?.store.name}</Text>
          </SkeletonText>

          {loaded && data?.store.city && (
            <HStack alignItems={"center"}>
              <Icon as={RiMapPin2Line} opacity={0.6} />
              <Text
                textStyle={"b-reg"}
                variant={"secondary"}
                textAlign={"center"}
              >
                {splitCityName(data.store.city)}
              </Text>
            </HStack>
          )}
        </VStack>

        {data && data.analytic && (
          <SimpleGrid
            className="simplegrid-card-info"
            flex={3}
            columns={[1, 2, 3]}
            gap={4}
          >
            <CardInfo
              title={"Dilihat hari ini"}
              value={formatNumber(data.analytic.viewToday)}
              icon={RiEye2Line}
              color="green"
              loaded={loaded}
            />

            <CardInfo
              title={"Terjual hari ini"}
              value={formatNumber(data.analytic.todaySold)}
              icon={RiStore2Line}
              color="yellow"
              loaded={loaded}
            />

            <CardInfo
              title={"Masuk Favorit"}
              value={formatNumber(data.analytic.totalFavorite)}
              icon={RiHeart2Line}
              color="red"
              loaded={loaded}
            />

            <CardInfo
              title={"Total Dilihat"}
              value={formatNumber(data.analytic.viewTotal)}
              icon={RiEyeLine}
              color="purple"
              loaded={loaded}
            />
            <CardInfo
              title={"Total Terjual"}
              value={formatNumber(data.analytic.totalSold)}
              icon={RiEyeLine}
              color="orange"
              loaded={loaded}
            />

            <CardInfo
              title={"Masuk Keranjang"}
              value={formatNumber(data.analytic.totalOnCart)}
              icon={RiEyeLine}
              color="teal"
              loaded={loaded}
            />
          </SimpleGrid>
        )}
      </Stack>

      <SimpleGrid
        className="wrap-product"
        w={"100%"}
        columns={[1, null, null, 2]}
        // direction={sw > 992 ? "row" : "column-reverse"}
        gap={4}
        mt={4}
      >
        <MyStoreProductInfo
          data={data?.product}
          order={[1, null, null, 0]}
          flex={1}
          loaded={loaded}
        />

        <Suspense fallback={<CardSkeleton row={1} height={400} />}>
          <AnalyticLineChart
            title="Analitik Viewer Produk"
            data={data?.analytic.analyticView}
            order={[0, null, null, 1]}
            flex={1}
          />
        </Suspense>
      </SimpleGrid>
    </VStack>
  );
};
